import { useEffect } from 'react';

import { InstallationOptionsEnum } from '@updater/consumer-graph';

import { Address, PiiTokenizedData, CreditCard } from 'flows/tvi/types';
import type { getUserInfo_getUser_user_currentMove_fromAddress as FromAddress } from 'types/generated/getUserInfo';
import { useUserData } from 'flows/core/hooks/use-profile';
import {
  ActivationDefaults,
  useGetEstimatedActivationDate,
} from 'flows/tvi/hooks/use-get-estimated-activation-date';
import { createFormStore } from '../common/form-store';
import { SMS_OPT_IN_VALUE } from 'flows/tvi/components/review/utils/constants';
import { useReservationAdapter } from 'flows/tvi/hooks/use-reservation-adapter';
import {
  Reservation,
  useBuyFlowContextOptional,
} from 'flows/tvi/generic-buyflow/contexts/BuyFlowContext';

export type CoxFormData = {
  internetModemChoice: string;
  installation?: {
    internetModemChoice: string;
  };
  ssn: string | PiiTokenizedData;
  dob: string | PiiTokenizedData;
  passcode: string | PiiTokenizedData;
  securityQuestion: string;
  securityAnswer: string | PiiTokenizedData;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  smsOptIn: 'sms'[];
  shippingAddress?: string;
  newShippingAddress?: Address;
  installationDate?: string;
  installationTimeWindow?:
    | '8am to 10am'
    | '10am to 12pm'
    | '1pm to 3pm'
    | '3pm to 5pm';
  installationType?: InstallationOptionsEnum;
  creditCard?: Partial<CreditCard>;
  /*
  This field is not UI visible
  It is used by informant to conditionally render the `Schedule Installation` Screen
  based if the user has self install enabled or not
*/
  priorAddress?: { address: Address };
  isInstantOn?: boolean;
};

export enum CoxPages {
  MODEM_CHOICE = 'modems',
  CURRENT_ADDRESS_LONGEVITY = 'current-address-longevity',
  IDENTITY = 'identity',
  SECURE_ACCOUNT = 'secure-account',
  SCHEDULE_INSTALLATION = 'schedule-installation',
  PAYMENT_OPTION = 'payment-option',
  PAYMENT = 'payment-method',
  REVIEW = 'buy/review',
  SUBMISSION = 'submission',
  ERROR = 'error',
}

export const omitKeysForLogging = [
  'dob',
  'ssn',
  'passcode',
  'securityQuestion',
  'securityAnswer',
  'creditCard',
];

const partialFormDataFromReservation = (
  reservation: Reservation,
  activationDefaults: ActivationDefaults
) => {
  const installationTypeFromReservation =
    reservation?.offer?.options?.installationOptions?.type;
  const defaultInstallWindowTime = activationDefaults.defaultTimeWindow;

  return {
    installationType:
      (installationTypeFromReservation as InstallationOptionsEnum) ??
      InstallationOptionsEnum.self,
    installationTimeWindow: defaultInstallWindowTime,
  };
};

const useStore = createFormStore<CoxFormData, CoxPages>({
  provider: 'cox',
  initialPageRoute: CoxPages.MODEM_CHOICE,
  initialFormData: {},
});

export function getCoxFormState() {
  return useStore.getState();
}

export const useCoxStore = (...args: Parameters<typeof useStore>) => {
  const status = useStore((state) => state.status);
  const initializeFormData = useStore((state) => state.initializeFormData);
  const { loading: userDataLoading, user } = useUserData();
  const activationDefaults = useGetEstimatedActivationDate();

  // Dual-mode: Use adapter hook and context
  const context = useBuyFlowContextOptional();
  const { reservation: reservationData, loading: reservationLoading } =
    useReservationAdapter();

  const hasHydrated = useStore.persist?.hasHydrated?.();

  useEffect(() => {
    if (
      hasHydrated &&
      status !== 'loaded' &&
      !userDataLoading &&
      !reservationLoading &&
      reservationData &&
      !activationDefaults.loading
    ) {
      const installationTypeFromReservation =
        (reservationData?.offer?.options?.installationOptions
          ?.type as InstallationOptionsEnum) ?? InstallationOptionsEnum.self;
      const isInstantOn = Boolean(
        reservationData?.offer?.planDetails?.instantOn
      );

      // Guest mode: Get customer info from context
      // Auth mode: Get customer info from user object
      let customerFirstName: string | undefined;
      let customerLastName: string | undefined;
      let customerEmail: string | undefined;
      let customerPhone: string | undefined;
      let customerAddress: Address | FromAddress | undefined;

      if (context?.isGuestMode && context?.customerInfo) {
        customerFirstName = context.customerInfo.firstName;
        customerLastName = context.customerInfo.lastName;
        customerEmail = context.customerInfo.email;
        customerPhone = context.customerInfo.phone;
        customerAddress = context.installAddress
          ? {
              street: context.installAddress.street,
              city: context.installAddress.city,
              state: context.installAddress.state,
              postalCode: context.installAddress.postalCode,
            }
          : undefined;
      } else {
        // Use nullish coalescing to filter out null values
        customerFirstName = user?.firstName ?? undefined;
        customerLastName = user?.lastName ?? undefined;
        customerEmail = user?.email ?? undefined;
        customerPhone = user?.phone ?? undefined;
        customerAddress = user?.currentMove?.fromAddress;
      }

      const initialData = {
        ...partialFormDataFromReservation(reservationData, activationDefaults),
        installationDate: activationDefaults.estimatedDate ?? '',
        installationType: installationTypeFromReservation,
        firstName: customerFirstName,
        lastName: customerLastName,
        email: customerEmail,
        phone: customerPhone,
        smsOptIn: [SMS_OPT_IN_VALUE],
        isInstantOn,
        creditCard: {
          firstName: customerFirstName,
          lastName: customerLastName,
          address: customerAddress,
        },
      } as Partial<CoxFormData>;

      initializeFormData({
        formData: initialData,
        offerName: reservationData?.offer?.name,
      });
    }
  }, [
    hasHydrated,
    status,
    userDataLoading,
    user,
    initializeFormData,
    reservationData,
    activationDefaults,
    reservationLoading,
    context,
  ]);

  return useStore(...args);
};
